import { uniqBy } from 'lodash'
import store from '@/store/index.js'

const candidatesFromContracts = (optionsForContract) => (items, field) => uniqBy(
  items.flatMap(item => (item.contracts ?? []).flatMap(c => optionsForContract(c, field))),
  'value')

const contractIsUnresolved = (item) => item.contractId === undefined

const SPECIES_CATEGORY = {
  phase: 'scaleData',
  profileArrayKey: 'speciesProfiles',
  externalNameField: 'externalSpeciesCode',
  idField: 'speciesId',
  rowValueFields: ['species'],
  titleKey: 'species',
  labelKey: 'species',
  alertKey: 'selectMappingForScaleDataImportValue',
  persistAction: 'scale-sheet/createProfileMapping',
  childPath: 'speciesprofiles',
  getCandidates: () => store.getters['species/allSpecies']
    .map(s => ({ value: s.speciesId, text: `${s.code} - ${s.commonName}` }))
}

const GRADE_CATEGORY = {
  phase: 'scaleData',
  profileArrayKey: 'gradeProfiles',
  externalNameField: 'externalGradeCode',
  idField: 'gradeId',
  rowValueFields: ['segmentOneGrade', 'segmentTwoGrade'],
  titleKey: 'grades',
  labelKey: 'grade',
  alertKey: 'selectMappingForScaleDataImportValue',
  persistAction: 'scale-sheet/createProfileMapping',
  childPath: 'gradeprofiles',
  isApplicable: (profile) => !profile.defaultGradeId,
  getCandidates: () => store.getters['grade/allGrades']
    .map(g => ({ value: g.gradeId, text: `${g.code} - ${g.name}` }))
}

const TRACT_CATEGORY = {
  phase: 'contract',
  profileArrayKey: 'tractProfiles',
  externalNameField: 'externalTractName',
  idField: 'tractId',
  rowValueFields: ['tract'],
  titleKey: 'tracts',
  labelKey: 'tractName',
  alertKey: 'selectMappingForImportValue',
  issueKey: 'importMappingIssueTract',
  getTicketIssueKey: ({ ticketNumber, optionValue, ticketBooks }) =>
    ticketBooks.some(tb => tb.tractId === optionValue && tb.firstPage <= ticketNumber && tb.lastPage >= ticketNumber)
      ? 'importMappingIssueTract'
      : 'importMappingIssueTicketBook',
  warningKey: 'importMappingWarning',
  persistAction: 'scale-sheet/createTractProfile',
  getCandidates: (items) => uniqBy(
    items.flatMap(item =>
      (item.contracts ?? [])
        .filter(c => c.tractId > 0)
        .map(c => ({ value: c.tractId, text: c.tract }))
    ),
    'value')
}

const ACCOUNT_CATEGORY = {
  phase: 'contract',
  profileArrayKey: 'accountProfiles',
  externalNameField: 'externalAccountName',
  idField: 'accountId',
  rowValueFields: ['contractAccount', 'destination'],
  titleKey: 'accounts',
  labelKey: 'accountName',
  alertKey: 'selectMappingForImportValue',
  issueKey: 'importMappingIssueAccount',
  warningKey: 'importMappingWarning',
  persistAction: 'scale-sheet/createProfileMapping',
  childPath: 'accountprofiles',
  logYardSaleContractsIgnoreMapping: true,
  isUnresolved: contractIsUnresolved,
  getCandidates: candidatesFromContracts((c, field) => {
    const option = field === 'destination'
      ? { value: c.destinationAccountId, text: c.destination }
      : { value: c.accountId, text: c.account }
    return option.value > 0 ? [option] : []
  })
}

const SETTING_CATEGORY = {
  phase: 'contract',
  profileArrayKey: 'settingProfiles',
  externalNameField: 'externalSettingName',
  idField: 'settingId',
  rowValueFields: ['setting'],
  titleKey: 'settings',
  labelKey: 'settingName',
  alertKey: 'selectMappingForImportValue',
  issueKey: 'importMappingIssueSetting',
  warningKey: 'importMappingWarning',
  persistAction: 'scale-sheet/createProfileMapping',
  childPath: 'settingprofiles',
  logYardSaleContractsIgnoreMapping: true,
  isApplicable: (profile) => !profile.defaultSettingId,
  isUnresolved: contractIsUnresolved,
  getCandidates: candidatesFromContracts(c => c.settingId > 0 ? [{ value: c.settingId, text: c.setting }] : [])
}

const PRODUCT_CATEGORY = {
  phase: 'contract',
  profileArrayKey: 'productProfiles',
  externalNameField: 'externalProductName',
  idField: 'productId',
  rowValueFields: ['product'],
  titleKey: 'products',
  labelKey: 'productName',
  alertKey: 'selectMappingForImportValue',
  issueKey: 'importMappingIssueProduct',
  warningKey: 'importMappingWarningMayFail',
  persistAction: 'scale-sheet/createProfileMapping',
  childPath: 'productprofiles',
  getCandidates: candidatesFromContracts(c => (c.products ?? []).map(p => ({ value: p.productId, text: p.name })))
}

// Order matters: scale-data codes must be mapped before the contract parse, and account/setting
// narrow the contracts that product candidates are read from.
export const IMPORT_PROFILE_MAPPING_CATEGORIES = [
  SPECIES_CATEGORY,
  GRADE_CATEGORY,
  TRACT_CATEGORY,
  ACCOUNT_CATEGORY,
  SETTING_CATEGORY,
  PRODUCT_CATEGORY
]
